import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AlertTriangle, ArrowLeft, Check, Network, Pause, Play, Upload } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import kgDatasetService from '../../services/kgDatasetService';
import PageLoader from '../shared/PageLoader';
import sharedStyles from '../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './KgDatasetDetail.module.scss';
import MappingTab from './tabs/MappingTab';
import LoadDataTab from './tabs/LoadDataTab';
import RecordsTab from './tabs/RecordsTab';
import FacetsTab from './tabs/FacetsTab';
import JobsTab from './tabs/JobsTab';
import KgSettingsTab from './tabs/KgSettingsTab';
import {
  INGEST_POLL_MS,
  INSPECT_SAMPLE_SIZE,
  activateBlockedReason,
  canActivate,
  describeError,
  failedCount,
  formatNumber,
  getDatasetStatusMeta,
  indexedCount,
  isIndexing,
  isNotFound,
  readInspection,
  recordCount,
  recordLabel,
  recordNoun,
} from './kgUtils';

const TABS = [
  { value: 'mapping', label: 'Mapping' },
  { value: 'data', label: 'Load data' },
  { value: 'records', label: 'Records' },
  { value: 'facets', label: 'Facets' },
  { value: 'history', label: 'History' },
  { value: 'settings', label: 'Settings' },
];

/**
 * Dataset detail — the shell that owns `dataset`, `facets` and the parsed sample,
 * and hands them to whichever tab is showing.
 *
 * Laid out as a tabbed page with a progress strip rather than a locked wizard:
 * the strip guides a first run (map → load → check → activate) while someone
 * coming back to a live dataset can jump straight to Records or History.
 *
 * "Check" sits BEFORE "Activate" on purpose. Browsing works in every status
 * including draft — the status gate stops agents seeing a dataset, not its
 * owner — which is what makes "look at it before you go live" a real step rather
 * than a leap of faith.
 */
const KgDatasetDetail = () => {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  const [dataset, setDataset] = useState(null);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  // A sample file drives the column pickers in the mapping editor. Either tab can
  // supply it; the shell holds it so switching tabs doesn't lose it. Arriving
  // straight from the create wizard, it is handed over in router state so the
  // live check is already running rather than asking for the same file again.
  const sampleHandover = location.state?.kgSample || null;
  const [samples, setSamples] = useState(() => sampleHandover?.records || null);
  // What /inspect made of that sample — the column vocabulary for every picker.
  const [inspection, setInspection] = useState(null);
  const [inspecting, setInspecting] = useState(false);

  const pollRef = useRef(null);
  const didAutoSelectTab = useRef(false);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  /**
   * The column vocabulary every path picker draws on.
   *
   * It comes from POST /inspect rather than from walking the sample here,
   * because the two do not agree: the server decodes JSON-string columns and
   * flattens nested shapes on the way in, so paths like `normalized.event_id`
   * exist for it and cannot be derived in the browser at all. Guessing locally
   * produced pickers full of paths that resolve to nothing.
   *
   * The dataset's own saved `preprocessing` is sent, so the paths offered are
   * the ones THIS dataset will actually see. A dataset with no normalization
   * sends `{}` rather than omitting the key — omitting it asks the server to
   * PROPOSE one, which would fill the pickers with `normalized.*` paths this
   * dataset never derives.
   */
  const preprocessingKey = JSON.stringify(dataset?.mapping_config?.preprocessing || {});

  useEffect(() => {
    if (!samples?.length) {
      setInspection(null);
      return undefined;
    }
    let cancelled = false;
    setInspecting(true);
    (async () => {
      try {
        const response = await kgDatasetService.inspect(
          samples.slice(0, INSPECT_SAMPLE_SIZE),
          { preprocessing: JSON.parse(preprocessingKey) }
        );
        if (!cancelled) setInspection(readInspection(response));
      } catch (e) {
        // The pickers degrade to free text, which still works — so this is a
        // quiet failure, not a reason to break the editor.
        if (!cancelled) setInspection(null);
      } finally {
        if (!cancelled) setInspecting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on the SERIALISED config: the dataset object is replaced on every
    // ingestion poll, and depending on the object itself would fire a fresh
    // inspect every few seconds for as long as embedding runs.
  }, [samples, preprocessingKey]);

  const sampleFields = inspection?.fields || [];

  // ── Data ────────────────────────────────────────────────────────────────

  const loadDataset = useCallback(async () => {
    try {
      const data = await kgDatasetService.getDataset(datasetId);
      setDataset(data);
      setNotFound(false);
      return data;
    } catch (e) {
      // 404 is "not found OR not yours" — deliberately indistinguishable, so
      // never render anything implying the dataset exists.
      if (isNotFound(e)) setNotFound(true);
      else setError(describeError(e, "Couldn't load this dataset."));
      return null;
    }
  }, [datasetId]);

  const loadFacets = useCallback(async () => {
    try {
      const data = await kgDatasetService.getFacets(datasetId);
      setFacets(data);
      return data;
    } catch (e) {
      // Facet coverage is supporting information — a failure here should not
      // take down the whole screen.
      setFacets(null);
      return null;
    }
  }, [datasetId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    didAutoSelectTab.current = false;

    (async () => {
      const d = await loadDataset();
      if (cancelled) return;
      if (d) await loadFacets();
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [loadDataset, loadFacets]);

  // The only thing that moves on its own: Celery embedding text after a push.
  // Records and facets committed synchronously, so filters and counts are
  // already right — this is purely the "searchable yet?" signal.
  const indexing = dataset ? isIndexing(dataset) : false;

  useEffect(() => {
    if (!indexing) return undefined;
    pollRef.current = setInterval(() => loadDataset(), INGEST_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [indexing, loadDataset]);

  // ── Tabs ────────────────────────────────────────────────────────────────

  const requestedTab = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.value === requestedTab) ? requestedTab : TABS[0].value;

  const goToTab = useCallback(
    (value) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // First arrival with no ?tab=: an empty dataset starts on Mapping, a loaded
  // one on the records browser.
  useEffect(() => {
    if (loading || !dataset || didAutoSelectTab.current) return;
    didAutoSelectTab.current = true;
    if (requestedTab) return;
    goToTab(recordCount(dataset) === 0 ? 'mapping' : 'records');
  }, [loading, dataset, requestedTab, goToTab]);

  // ── Mutations ───────────────────────────────────────────────────────────

  /** Every edit funnels through here so the header and facet catalog stay in step. */
  const handleMutated = useCallback(
    async ({ reloadDataset = true, reloadFacets = false } = {}) => {
      const jobs = [];
      if (reloadDataset) jobs.push(loadDataset());
      if (reloadFacets) jobs.push(loadFacets());
      await Promise.all(jobs);
    },
    [loadDataset, loadFacets]
  );

  const handleSetStatus = async (status) => {
    setStatusBusy(true);
    try {
      await kgDatasetService.setStatus(datasetId, status);
      const messages = {
        active: 'Live — agents attached to this dataset can query it now.',
        paused: 'Paused. Agents can no longer query it; nothing has been deleted.',
        archived: 'Archived.',
        draft: 'Back to draft — agents can no longer see it.',
      };
      notify(messages[status] || 'Status updated.', status === 'active' ? 'success' : 'info');
      await handleMutated({ reloadFacets: true });
    } catch (e) {
      notify(describeError(e, "Couldn't change the status."), 'error');
    } finally {
      setStatusBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) return <PageLoader message="Loading dataset..." />;

  if (notFound) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className={sharedStyles.layout}>
          <div className={sharedStyles.inner}>
            <div className={sharedStyles.emptyState}>
              <Network size={44} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">This dataset doesn’t exist</Typography>
              <Typography variant="body2" className={sharedStyles.muted}>
                It may have been deleted, or the link is wrong.
              </Typography>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                onClick={() => navigate('/knowledge-graph')}
                sx={{ mt: 2 }}
              >
                Back to Knowledge Graph
              </Button>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const statusMeta = getDatasetStatusMeta(dataset);
  const records = recordCount(dataset);
  const indexed = indexedCount(dataset);
  const failed = failedCount(dataset);
  const facetCount = facets?.facets?.length ?? 0;
  const blockedReason = activateBlockedReason(dataset);

  const steps = [
    {
      key: 'mapping',
      label: 'Map',
      done: Boolean(dataset?.mapping_config?.facets?.length),
      onClick: () => goToTab('mapping'),
    },
    { key: 'data', label: 'Load', done: records > 0, onClick: () => goToTab('data') },
    {
      key: 'records',
      label: 'Check',
      done: records > 0 && dataset.status !== 'draft',
      onClick: () => goToTab('records'),
    },
    {
      key: 'activate',
      label: 'Activate',
      done: dataset.status === 'active',
      onClick: () =>
        dataset.status === 'active'
          ? goToTab('settings')
          : canActivate(dataset)
            ? handleSetStatus('active')
            : notify(blockedReason, 'info'),
    },
  ];
  const currentStepIndex = Math.max(0, steps.findIndex((s) => !s.done));

  const renderTab = () => {
    const shared = {
      dataset,
      facets,
      samples,
      sampleFields,
      inspection,
      inspecting,
      onSamples: setSamples,
      sampleHandover,
      onMutated: handleMutated,
      notify,
      goToTab,
    };
    switch (activeTab) {
      case 'mapping':
        return <MappingTab {...shared} />;
      case 'data':
        return <LoadDataTab {...shared} />;
      case 'records':
        return <RecordsTab {...shared} />;
      case 'facets':
        return <FacetsTab {...shared} />;
      case 'history':
        return <JobsTab {...shared} />;
      case 'settings':
        return <KgSettingsTab {...shared} onSetStatus={handleSetStatus} statusBusy={statusBusy} />;
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.inner}>
          {/* ── Header ── */}
          <div className={styles.header}>
            <Button
              className={sharedStyles.backBtn}
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/knowledge-graph')}
            >
              Knowledge Graph
            </Button>

            <div className={styles.headerMain}>
              <div className={styles.headerTitle}>
                <span className={styles.headerIcon}>
                  <Network size={20} />
                </span>
                <div className={styles.headerText}>
                  <Typography variant="h5" className={styles.name}>
                    {dataset.label || 'Untitled dataset'}
                  </Typography>
                  <div className={styles.headerMeta}>
                    <code className={styles.keyTag}>{dataset.key}</code>
                    <Tooltip title={statusMeta.meaning}>
                      <span className={`${styles.pill} ${styles[statusMeta.tone]}`}>
                        {statusMeta.label}
                      </span>
                    </Tooltip>
                    <span className={styles.metaItem}>{recordLabel(dataset, records)}</span>
                    {facetCount > 0 && (
                      <span className={styles.metaItem}>
                        {facetCount} facet{facetCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {failed > 0 && (
                      <button
                        type="button"
                        className={styles.failedChip}
                        onClick={() => goToTab('history')}
                      >
                        <AlertTriangle size={12} />
                        {formatNumber(failed)} failed
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.headerActions}>
                {dataset.status === 'active' ? (
                  <Button
                    className={sharedStyles.outlineBtn}
                    variant="outlined"
                    startIcon={statusBusy ? <CircularProgress size={14} /> : <Pause size={15} />}
                    onClick={() => handleSetStatus('paused')}
                    disabled={statusBusy}
                  >
                    Pause
                  </Button>
                ) : !canActivate(dataset) ? (
                  // A disabled primary button is the wrong control here twice
                  // over: it explains nothing, and `.primaryBtn` forces its
                  // background, so MUI's disabled state only dims it slightly and
                  // it still reads as clickable. Offer the actual next step
                  // instead — which is what the dataset list card already does.
                  <Tooltip title={blockedReason}>
                    <Button
                      className={sharedStyles.outlineBtn}
                      variant="outlined"
                      startIcon={<Upload size={15} />}
                      onClick={() => goToTab('data')}
                    >
                      Load data to activate
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    className={sharedStyles.primaryBtn}
                    variant="contained"
                    startIcon={statusBusy ? <CircularProgress size={14} /> : <Play size={15} />}
                    onClick={() => handleSetStatus('active')}
                    disabled={statusBusy}
                  >
                    Activate
                  </Button>
                )}
              </div>
            </div>

            {/* ── Ingestion progress: loaded vs searchable ── */}
            {indexing && (
              <div className={styles.ingestBar}>
                <LinearProgress
                  variant="determinate"
                  value={records ? Math.min(100, (indexed / records) * 100) : 0}
                  className={styles.ingestProgress}
                />
                <span className={styles.ingestLabel}>
                  Loading text: {formatNumber(indexed)} of {formatNumber(records)}{' '}
                  {recordNoun(dataset, records)} — filters and counts already work, semantic
                  search catches up as this finishes.
                </span>
              </div>
            )}

            {/* Draft-with-records: everything looks right and the agent still
                has no KG tools. Say so where it cannot be missed. */}
            {dataset.status === 'draft' && records > 0 && (
              <Alert severity="warning" className={styles.draftAlert}>
                <strong>Not yet visible to agents.</strong> This dataset is still a draft, so an
                agent with its folder attached gets no knowledge-graph tools. Browse the records
                to check they look right, then activate.
              </Alert>
            )}
            {dataset.status === 'paused' && (
              <Alert severity="info" className={styles.draftAlert}>
                <strong>Paused.</strong> Agents cannot query this dataset. Nothing has been
                deleted and you can still browse it.
              </Alert>
            )}

            {/* ── Progress strip: guidance, not a lock ── */}
            <div className={styles.steps}>
              {steps.map((step, index) => (
                <button
                  key={step.key}
                  type="button"
                  className={`${styles.step} ${step.done ? styles.stepDone : ''} ${
                    index === currentStepIndex && !step.done ? styles.stepCurrent : ''
                  }`}
                  onClick={step.onClick}
                >
                  <span className={styles.stepMark}>
                    {step.done ? <Check size={12} /> : index + 1}
                  </span>
                  {step.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, value) => goToTab(value)}
            className={sharedStyles.consoleTabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          <div className={styles.tabBody}>{renderTab()}</div>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default KgDatasetDetail;
